import { useCallback, useEffect, useMemo } from "react";
import { useTheme, Button, Typography, useMediaQuery, Box } from "@mui/material";
import { alpha } from "@mui/system";
import { EnhancedTable } from "@mb-pro-ui/components/table";
import { Column, Row } from "react-table";
import { FormatDateOptions, useIntl } from "react-intl";

import { useDevice } from "../utils/DeviceProvider";
import { ConfigHistory } from "../utils/types";
import { useServerCommunication } from "../utils/ServerCommunicaton";
import { CellProps } from "react-table";

interface ConfigHistoryPageProps {
  loadConfig: (olderConfig: any) => void;
}

const dateFormatOptions: FormatDateOptions = {
  dateStyle: "short",
  timeStyle: "medium",
};

const ConfigHistoryPage = ({ loadConfig }: ConfigHistoryPageProps) => {
  const {
    device: { configHistory, unitid },
    updateConfigHistory,
  } = useDevice();

  const theme = useTheme();
  const { formatMessage, formatDate } = useIntl();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { getConfigById } = useServerCommunication();

  const applyConfig = useCallback(
    (configId: number) => {
      return getConfigById(unitid, configId).then((res) => loadConfig(res?.toolboxConfig));
    },
    [loadConfig, getConfigById, unitid]
  );

  useEffect(() => {
    updateConfigHistory();
  }, [updateConfigHistory]);

  const getStyle = useCallback(
    (_item: ConfigHistory) => {
      let color = theme.palette.common.black;
      let backgroundColor = theme.palette.common.white;

      return { color, backgroundColor };
    },
    [theme]
  );

  const rowProps = useCallback(
    (row: Row<ConfigHistory>) => ({
      style: {
        ...getStyle(row.original),
        "&:hover": {
          backgroundColor: (theme: any) => `${alpha(theme.palette.primary.main, 0.2)}`,
        },
      },
    }),
    [getStyle]
  );

  const columnsNarrow = useMemo(
    (): Column<ConfigHistory>[] => [
      {
        id: "configVersion",
        Header: formatMessage({
          defaultMessage: "Version",
        }),
        accessor: (item) => item.configVersion,
        width: 30,
      },
      {
        id: "modifiedTs",
        Header: formatMessage({
          defaultMessage: "Modified",
        }),
        accessor: (item) => formatDate(item.modifiedTs, dateFormatOptions),
        width: 80,
        Cell: ({ row }: CellProps<ConfigHistory>) => (
          <Box>
            <Typography component={"span"} sx={{ display: "block" }}>
              {formatDate(row.original.modifiedTs, dateFormatOptions)}
            </Typography>
            <Typography component={"span"} sx={{ display: "block" }}>
              {row.original.modifierFullname || row.original.modifierName}
            </Typography>
          </Box>
        ),
      },
      {
        id: "configinfo",
        Header: formatMessage({
          defaultMessage: "Configuration info",
        }),
        accessor: (item) => [item.accountinfo?.account, item.accountinfo?.name, item.accountinfo?.description].filter((i) => !!i).join(", "),
        width: 100,
        Cell: ({ row }: CellProps<ConfigHistory>) => (
          <Box>
            <Typography component={"span"} sx={{ display: "block" }}>
              {[row.original.accountinfo?.account, row.original.accountinfo?.name].filter((i) => !!i).join(", ")}
            </Typography>
            <Typography component={"span"} sx={{ display: "block" }}>
              {row.original.accountinfo?.description}
            </Typography>
          </Box>
        ),
      },
    ],
    [formatMessage, formatDate]
  );

  const columns = useMemo(
    (): Column<ConfigHistory>[] => [
      {
        id: "configVersion",
        Header: formatMessage({
          defaultMessage: "Version",
        }),
        accessor: (item) => item.configVersion,
        width: 30,
      },
      {
        id: "modifiedTs",
        Header: formatMessage({
          defaultMessage: "Modified",
        }),
        accessor: (item) => formatDate(item.modifiedTs, dateFormatOptions),
        width: 70,
      },
      // {
      //   id: "syncedTs",
      //   Header: formatMessage({
      //     defaultMessage: "Notified about configuration",
      //   }),
      //   accessor: (item) => (item.syncedTs ? formatDate(item.syncedTs, dateFormatOptions) : "-"),
      //   width: 75,
      // },
      {
        id: "modifier",
        Header: formatMessage({
          defaultMessage: "Modifier",
        }),
        accessor: (item) => item.modifierFullname || item.modifierName,
        width: 70,
      },
      {
        id: "configName",
        Header: formatMessage({
          defaultMessage: "Account, name",
        }),
        accessor: (item) => [item.accountinfo?.account, item.accountinfo?.name].filter((i) => !!i).join(", "),
        width: 70,
      },
      {
        id: "configDescription",
        Header: formatMessage({
          defaultMessage: "Description",
        }),
        accessor: (item) => item.accountinfo?.description,
        width: 100,
      },
    ],
    [formatMessage, formatDate]
  );

  return (
    <EnhancedTable
      columns={smallScreen ? columnsNarrow : columns}
      data={configHistory?.filter(({ active }) => !active) ?? []}
      showResultCount
      action={(row) => <Button onClick={() => applyConfig(row.original.id)}>{formatMessage({ defaultMessage: "Apply config" })}</Button>}
      actionPosition="left"
      actionCellWidth={120}
      getRowProps={rowProps}
      queryStatus={configHistory ? "success" : "loading"}
      overscanCount={5}
      rowHeight={smallScreen ? 52 : undefined}
      limit={1000}
    />
  );
};

export default ConfigHistoryPage;
